"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const index_1 = __importDefault(require("./index"));
const functional_1 = require("../functional");
const reporter_1 = __importDefault(require("../../reporter"));
const modelData = (models, modelIds) => {
    try {
        const { id, index } = modelIds[0];
        return models[index][id];
    }
    catch (e) {
        reporter_1.default.warn(`Missing model data`);
        return '';
    }
};
// Helper functions for accessing and resolving values
const getValue = data => ast => ast.value !== undefined ? ast.value : (0, index_1.default)({ ast, data });
const getModelData = (models, modelIds) => modelData(models, modelIds);
const getArgNum = n => ast => {
    const { args } = ast;
    const output = args[n - 1];
    if (output === undefined) {
        throw new Error(`Missing argument #${n} in function '${ast.func}', existing arguments: ${JSON.stringify(ast.args)}`, ast);
    }
    return output;
};
const getValueForArg = (argNum, data) => ast => (0, functional_1.pipe)(ast)(getArgNum(argNum), getValue(data));
const getAllArgValues = (ast, data) => ast.args.map(getValue(data));
// Function implementations below
// Round a number
const round = ({ ast, data }) => (0, functional_1.pipe)(ast)(getValueForArg(1, data), Math.round);
// Get the ceiling for a number
const ceiling = ({ ast, data }) => (0, functional_1.pipe)(ast)(getValueForArg(1, data), Math.ceil);
const choose = ({ ast, data }) => getValueForArg(1, data)(ast)
    ? getValueForArg(2, data)(ast)
    : getValueForArg(3, data)(ast);
// Negate a value
const not = ({ ast, data }) => !getValueForArg(1, data)(ast);
// Negate a number
const neg = ({ ast, data }) => -getValueForArg(1, data)(ast);
// Test if a string starts with a value (returns boolean)
const startsWith = ({ ast, data }) => {
    const str = (0, functional_1.pipe)(ast)(getValueForArg(1, data));
    return typeof str === 'string'
        ? str.startsWith(getValueForArg(2, data)(ast))
        : false;
};
// Get length of a string
const stringLength = ({ ast, data }) => {
    const str = getValueForArg(1, data)(ast);
    return typeof str === 'string' ? str.length : null;
};
// Checks if a string contians any of a number of comma-separated values (returns boolean)
const trimStr = str => (typeof str === 'string' ? str.trim() : str);
const split = delimiter => str => str.split(delimiter);
const splitContainsValue = value => (0, functional_1.pipe)(value)(split(','), (0, functional_1.map)(trimStr));
const contains = ({ ast, data }) => {
    const needle = getValueForArg(2, data)(ast);
    return (0, functional_1.pipe)(ast)(getValueForArg(1, data), splitContainsValue, (0, functional_1.some)(str => str === needle));
};
const containsAny = ({ ast, data }) => {
    const [csvHaystack, ...needles] = getAllArgValues(ast, data);
    return (0, functional_1.pipe)(csvHaystack)(splitContainsValue, (0, functional_1.some)(str => needles.includes(str)));
};
// Returns a substring of a string from index to a specified length
const substring = ({ ast, data }) => {
    const str = getValueForArg(1, data)(ast);
    // Note: xpath substring indexes from `1, so subtracting 1 below
    return typeof str === 'string'
        ? str.substr(getValueForArg(2, data)(ast) - 1, getValueForArg(3, data)(ast))
        : null;
};
// Casts a string or boolean to a number
const number = ({ ast, data }) => {
    const val = getValueForArg(1, data)(ast);
    if (typeof val === 'string') {
        return parseFloat(val);
    }
    else if (typeof val === 'boolean') {
        return val ? 1 : 0;
    }
    return val;
};
// Concats an arbitrary number of strings together
const join = str => arr => arr.join(str);
const concat = ({ ast, data }) => (0, functional_1.pipe)(ast.args)((0, functional_1.map)(v => getValue(data)(v)), join(''));
const position = ({ data: { position: pos } }) => pos || 1;
const index = ({ data: { repeatIndex } }) => repeatIndex || 1;
// Trims whitespace from a string
const trim = ({ ast, data }) => (0, functional_1.pipe)(ast)(getValueForArg(1, data), trimStr);
// Normalizes spaces (max one space) and trims string
const replace = (needle, withStr) => str => str.replace(needle, withStr);
const normalizeSpace = ({ ast, data }) => (0, functional_1.pipe)(ast)(getValueForArg(1, data), trimStr, replace(/\s\s+/g, ' '));
const lowerCase = ({ ast, data }) => {
    const str = getValueForArg(1, data)(ast);
    return str != null ? str.toString().toLowerCase() : '';
};
const upperCase = ({ ast, data }) => {
    const str = getValueForArg(1, data)(ast);
    return str != null ? str.toString().toUpperCase() : '';
};
// Returns substring of string after first match of needle
const sliceAfterIndex = (str, needle) => str.indexOf(needle) + needle.length;
const substringAfter = ({ ast, data }) => {
    const str = getValueForArg(1, data)(ast);
    const needle = getValueForArg(2, data)(ast);
    return str.slice(sliceAfterIndex(str, needle));
};
// Returns substring of string before first match of needle
const substringBefore = ({ ast, data }) => {
    const str = getValueForArg(1, data)(ast);
    const needle = getValueForArg(2, data)(ast);
    return str.slice(0, str.indexOf(needle));
};
// Returns current date
const now = () => new Date();
const getYear = ({ ast, data }) => {
    const date = ast.args[0] ? getValueForArg(1, data)(ast) : new Date();
    return date.getFullYear();
};
// Returns days since epoch as a number
const daysSinceEpoch = date => Math.floor(date / 8.64e7);
const daysFromDate = ({ ast, data }) => (0, functional_1.pipe)(ast)(getValueForArg(1, data), daysSinceEpoch);
// Adjusts date by specified days, returns new date
const addDaysToDate = days => date => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
};
const adjustDateByDays = ({ ast, data }) => {
    const days = getValueForArg(2, data)(ast);
    return (0, functional_1.pipe)(ast)(getValueForArg(1, data), addDaysToDate(days));
};
const throwMissingFnError = fn => {
    const error = new Error(`Missing function: ${fn}`);
    throw error;
};
const validFn = ({ ast, data: { models } }) => {
    const { meta } = getModelData(models, getArgNum(1)(ast).modelIds);
    return !meta || !meta.validationErrors || meta.validationErrors.length < 1;
};
const requiredFn = ({ ast, data: { models } }) => {
    const relevantData = getModelData(models, getArgNum(1)(ast).modelIds);
    return !!(relevantData.meta && relevantData.meta.required);
};
const regexReplace = ({ ast, data }) => {
    const str = getValueForArg(1, data)(ast);
    const pattern = getValueForArg(2, data)(ast);
    const flags = getValueForArg(3, data)(ast);
    const newSubStr = getValueForArg(4, data)(ast);
    return str.replace(RegExp(pattern, flags), newSubStr);
};
const max = ({ ast, data }) => {
    const argValues = getAllArgValues(ast, data)
        .map(parseFloat)
        // Filter out NaN values
        .filter(val => val === val);
    return Math.max(...argValues);
};
const min = ({ ast, data }) => {
    const argValues = getAllArgValues(ast, data)
        .map(parseFloat)
        // Filter out NaN values
        .filter(val => val === val);
    return Math.min(...argValues);
};
const functionMap = fn => ({
    round,
    ceiling,
    choose,
    not,
    neg,
    'starts-with': startsWith,
    'string-length': stringLength,
    contains,
    'contains-any': containsAny,
    substring,
    number,
    concat,
    position,
    index,
    'fn:trim': trim,
    'fn:normalize-space': normalizeSpace,
    'fn:lower-case': lowerCase,
    'fn:upper-case': upperCase,
    'substring-after': substringAfter,
    'substring-before': substringBefore,
    now,
    'get-year': getYear,
    'days-from-date': daysFromDate,
    'adjust-date-by-days': adjustDateByDays,
    'icsforms:adjust-date-by-days': adjustDateByDays,
    'icsforms:required': requiredFn,
    'icsforms:valid': validFn,
    'regexp:replace': regexReplace,
    'fn:max': max,
    'fn:min': min,
}[fn] || (() => throwMissingFnError(fn)));
const operatorFn = (ast, data) => {
    const { func } = ast;
    return functionMap(func)({ ast, data });
};
exports.default = operatorFn;
// https://sagesure-software.slack.com/archives/C04MSA94V62/p1675437687608199
//# sourceMappingURL=execute-function.js.map